import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import SuggestionList from "./SuggestionList";
import "./ProductBasicInfo.css";
import { useLazyQuery } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import { debounce } from "lodash";

const GET_SUGGEST_BRANDS = gql`
  query getSuggestBrandsByTitle($title: String!) {
    getSuggestBrandsByTitle(title: $title) {
      id
      name
    }
  }
`;

const GET_SUGGEST_CATEGORIES = gql`
  query getSuggestCategoriesByTitle($title: String!, $brand: String) {
    getSuggestCategoriesByTitle(title: $title, brand: $brand) {
      gender
      category
      subcategory
      subcategory_id
    }
  }
`;

const MAX_TITLE_LENGTH = 40;
const MAX_DESCRIPTION_LENGTH = 2500;

const ProductBasicInfo = ({ register, watch, errors, trigger, setValue }) => {
  const title = watch("title") || "";
  const description = watch("description") || "";
  const brandFromForm = watch("brand") || "";

  const [suggestedBrands, setSuggestedBrands] = useState([]);
  const [suggestedCategories, setSuggestedCategories] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedBrandIndex, setSelectedBrandIndex] = useState(-1);
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(-1);

  const [getBrands, { data: brandsData, loading: brandsLoading }] =
    useLazyQuery(GET_SUGGEST_BRANDS);
  const [getCategories, { data: categoriesData, loading: categoriesLoading }] =
    useLazyQuery(GET_SUGGEST_CATEGORIES);

  useEffect(() => {
    if (brandsData && brandsData.getSuggestBrandsByTitle) {
      setSuggestedBrands(brandsData.getSuggestBrandsByTitle);
      setSelectedBrandIndex(-1); // 새로운 추천 목록이 로드되면 선택 초기화
    }
  }, [brandsData]);

  useEffect(() => {
    if (categoriesData && categoriesData.getSuggestCategoriesByTitle) {
      setSuggestedCategories(categoriesData.getSuggestCategoriesByTitle);
      setSelectedCategoryIndex(-1); // 새로운 추천 목록이 로드되면 선택 초기화
    }
  }, [categoriesData]);

  const debouncedSearch = useCallback(
    debounce((searchTerm) => {
      if (searchTerm.length >= 2) {
        getBrands({ variables: { title: searchTerm } });
        getCategories({
          variables: {
            title: searchTerm,
            brand: brandFromForm || null,
          },
        });
        setShowSuggestions(true);
      } else {
        setShowSuggestions(false);
      }
    }, 500),
    [brandFromForm],
  );

  const handleTitleChange = async () => {
    await trigger("title");
    const currentTitle = watch("title") || "";
    debouncedSearch(currentTitle);
  };

  const handleDescriptionChange = async () => {
    await trigger("description");
  };

  const handleSelectBrand = (brand, index) => {
    if (selectedBrandIndex === index) {
      setValue("brand", "");
      setSelectedBrandIndex(-1);

      const currentTitle = watch("title") || "";
      if (currentTitle.length >= 2) {
        getCategories({
          variables: {
            title: currentTitle,
            brand: null,
          },
        });
      }
    } else {
      setValue("brand", brand.name);
      setSelectedBrandIndex(index);

      const currentTitle = watch("title") || "";
      if (currentTitle.length >= 2) {
        getCategories({
          variables: {
            title: currentTitle,
            brand: brand.name,
          },
        });
      }
    }
  };

  const handleSelectCategory = (category, index) => {
    // 이미 선택된 카테고리를 다시 클릭한 경우 선택 취소
    if (selectedCategoryIndex === index) {
      setValue("gender", "");
      setValue("category", "");
      setValue("subCategory", "");
      setSelectedCategoryIndex(-1);
    } else {
      setValue(
        "gender",
        category.gender === "men"
          ? "남성"
          : category.gender === "women"
            ? "여성"
            : "공용",
      );
      setValue("category", category.category);
      setValue("subCategory", category.subcategory);
      setSelectedCategoryIndex(index);
    }
  };

  return (
    <div className="ProductBasicInfo-container">
      <div className="input-container">
        <div className="input-header">
          <label htmlFor="title">제목</label>
        </div>
        <input
          id="title"
          type="text"
          className={`form-input ${title.length > MAX_TITLE_LENGTH ? "form-input--error" : ""}`}
          placeholder="상품 제목을 입력해주세요"
          {...register("title", {
            required: "제목을 입력해주세요",
            maxLength: {
              value: MAX_TITLE_LENGTH,
              message: `최대 ${MAX_TITLE_LENGTH}자까지 입력 가능합니다`,
            },
            onChange: handleTitleChange,
          })}
        />
        <div className="form-footer">
          <p className="error-message">
            {errors.title ? errors.title.message : ""}
          </p>
          <span
            className={`char-count ${title.length > MAX_TITLE_LENGTH ? "char-count--exceeded" : ""}`}
          >
            {MAX_TITLE_LENGTH - title.length}
          </span>
        </div>
      </div>

      <SuggestionList
        showSuggestions={showSuggestions}
        title={title}
        suggestedBrands={suggestedBrands}
        suggestedCategories={suggestedCategories}
        selectedBrandIndex={selectedBrandIndex}
        selectedCategoryIndex={selectedCategoryIndex}
        brandsLoading={brandsLoading}
        categoriesLoading={categoriesLoading}
        onSelectBrand={handleSelectBrand}
        onSelectCategory={handleSelectCategory}
      />

      <div className="input-container">
        <div className="input-header">
          <label htmlFor="description">설명</label>
        </div>
        <textarea
          id="description"
          className={`form-textarea ${description.length > MAX_DESCRIPTION_LENGTH ? "form-textarea--error" : ""}`}
          placeholder="상품에 대해 자세히 적어주세요. (2500자 이내)"
          rows="10"
          {...register("description", {
            required: "상품 설명을 입력해주세요",
            minLength: {
              value: 10,
              message: "최소 10자 이상 입력해주세요",
            },
            maxLength: {
              value: MAX_DESCRIPTION_LENGTH,
              message: `최대 ${MAX_DESCRIPTION_LENGTH}자까지 입력 가능합니다`,
            },
            onChange: handleDescriptionChange,
          })}
        />
        <div className="form-footer">
          <p className="error-message">
            {errors.description ? errors.description.message : ""}
          </p>
          <span
            className={`char-count ${description.length > MAX_DESCRIPTION_LENGTH ? "char-count--exceeded" : ""}`}
          >
            {MAX_DESCRIPTION_LENGTH - description.length}
          </span>
        </div>
      </div>
    </div>
  );
};

ProductBasicInfo.propTypes = {
  register: PropTypes.func.isRequired,
  watch: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  trigger: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
};

export default ProductBasicInfo;
